import { Link } from "~/common/components/Link";
import { useTranslation } from "react-i18next";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { calculateSalesCampaign4PDP } from "~/features/salesCampaign/utils/salesCampaignUtils";
import type { SrpProductData } from "~/utils/models";
import { SALES_CAMPAIGN_PAGE } from "~/utils/url/staticUrls";
import {
  ProductRecommendationSection,
  ProductRecommendationTitle,
} from "~/features/saleRecommendations/components/ProductRecommendationSection";
import { ProductRecoType } from "~/features/saleRecommendations/models/ProductRecoType";
import { type ProductData } from "~/domains/product/types/ProductData";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { Skeleton, styled } from "~/common/mui";
import { FONT_RAW_SIZES, FontSize } from "~/common/components/materialInput/Fonts";
import { usePriceReferenceSelection, useSession } from "~/app/config/useSession";

export interface SimilarSalesProps {
  className?: string;
  products?: SrpProductData[];
  variant: "SRP" | "PDP";
  loading?: boolean;
}

export const CampaignRecommendations = ({ className, products, variant, loading }: SimilarSalesProps) => {
  const { t } = useTranslation();
  const { selection } = usePriceReferenceSelection();
  const activeSalesCampaign = useSession()?.salesCampaign?.activeSalesCampaigns[0];
  // As per SCHAEFF-33361, Max 4 products
  const productCodes = products?.slice(0, 4).map((product) => product.code) ?? [];

  let productDescriptors = Object.values(
    ProductService.useMultipleProductDescriptors({
      productCodes,
      priceReference: getInitialPriceReference(selection),
      options: { fetch: true },
    }),
  );

  const activeSalesCampaignUrl = activeSalesCampaign?.urlMapping;
  const pageUrl = [SALES_CAMPAIGN_PAGE, activeSalesCampaignUrl].filter(Boolean).join("/");

  productDescriptors = productDescriptors.filter((productDescriptor) => {
    const salesMap = calculateSalesCampaign4PDP({ productDescriptor });
    if (salesMap.status !== "loaded") {
      return true;
    }
    const { salePerPack } = salesMap.data;
    const entries = Object.entries(salePerPack);
    return entries.length > 0;
  });

  if (!productDescriptors.length && !loading) {
    return null;
  }

  const getTrackingAttributes = (product: ProductData, productDescriptor: ProductDescriptor) => {
    const salesMap = calculateSalesCampaign4PDP({
      productDescriptor,
    });
    const salesCampaignCode = salesMap.data?.salePerPack
      ? Object.values(salesMap.data?.salePerPack)[0]?.campaignCode
      : null;
    const trackingInfix = variant === "PDP" ? "spos" : "cob";
    const rootTrackingAttributes = {
      [`tracking-aux-${trackingInfix}-product-parent-marker`]: "true",
      [`tracking-aux-${trackingInfix}-is-campaign-belonging`]: salesCampaignCode ? "true" : false,
      [`tracking-aux-${trackingInfix}-campaign`]: salesCampaignCode ?? "None",
    };
    const linkProps = {
      [`tracking-aux-${trackingInfix}-product-name`]: product.designation,
      [`tracking-aux-${trackingInfix}-product-id`]: product.sapMaterialNumber,
      target: "_blank",
    };
    const buttonProps = {
      [`tracking-aux-${trackingInfix}-add-to-cart-button`]: "true",
      reco_type: "spos",
    };
    return { rootTrackingAttributes, linkProps, buttonProps };
  };

  let title = (
    <ProductRecommendationTitle>
      {t(`feature.salesPush.similarProducts.title.${variant.toLowerCase()}`)}
    </ProductRecommendationTitle>
  );
  if (loading) {
    title = (
      <Skeleton height={FONT_RAW_SIZES[FontSize.HeadlineSmall].fontSize} width={200} sx={{ color: "text.primary" }} />
    );
  }

  return (
    <ProductRecommendationSection
      className={className}
      title={
        <TitleRow>
          {title}
          {!loading && (
            <Link href={pageUrl} target={"_blank"} tracking-aux-cob-show-all-products-link-marker={"true"}>
              {t("feature.salesPush.similarProducts.viewAll")}
            </Link>
          )}
        </TitleRow>
      }
      loading={loading}
      loadingLabel={t("feature.salesPush.similarProducts.loading")}
      products={productCodes}
      variant={ProductRecoType[`campaign${variant}`]}
      getTrackingAttributes={getTrackingAttributes}
    />
  );
};

const TitleRow = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "32px",
  [theme.breakpoints.down("desktop-s")]: {
    marginBottom: "16px",
  },
}));
